import { useMemo, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader, type PageCreateAction } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { Button } from '@/components/ui/button';
import { batchesInfiniteQueryOptions } from '../api/publish.api';
import { PUBLISH_PANEL_PATH } from '../constants/publish.constants';
import { useBatchFilters, type BatchPanelStage } from '../hooks/useBatchFilters';
import { BatchFilterPanel } from './BatchFilterPanel';
import { getBatchColumns, type BatchRowAction } from './BatchColumns';

interface BatchPanelPageProps {
  stage: BatchPanelStage;
  title: string;
  emptyMessage: string;
  rowActions?: readonly BatchRowAction[] | undefined;
  createAction?: PageCreateAction | undefined;
  children?: ReactNode;
}

export function BatchPanelPage({
  stage,
  title,
  emptyMessage,
  rowActions,
  createAction,
  children,
}: BatchPanelPageProps) {
  const controller = useBatchFilters(stage);
  const { search } = controller;
  const { rows, isLoading, loadError, refetch, loadMoreProps } = useInfiniteTableQuery(
    batchesInfiniteQueryOptions(stage, search),
  );

  const { searchText, setSearchText } = useUrlSyncedSearch({
    appliedText: controller.appliedSearchText,
    onApply: controller.applySearchText,
  });

  const isNarrowed =
    controller.activeFilterCount > 0 || (controller.appliedSearchText ?? '') !== '';

  function resetFilters() {
    controller.applyFilterValues?.({});
    controller.applySearchText(undefined);
    setSearchText('');
  }

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const lastViewedBatchId = useRevealLastViewedRow('publishBatches');
  const totals = useMemo(() => {
    const first = rows[0];
    if (first === undefined) return undefined;
    return {
      dcrAmount: first.allDcrAmount,
      derAmount: first.allDerAmount,
      totalAmount: first.allDcrAmount + first.allDerAmount,
      specimenCount: first.allSpecimenCount,
    };
  }, [rows]);

  const columns = useMemo(
    () =>
      getBatchColumns(
        (batch) => (
          <Link
            to="/publish/batches/$batchId"
            params={{ batchId: batch.batchId }}
            search={{ from: PUBLISH_PANEL_PATH[stage] }}
          />
        ),
        controller.appliedSearchText,
        rowActions,
        totals,
      ),
    [stage, rowActions, controller.appliedSearchText, totals],
  );

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title={title}
        titleAddon={
          <DateRangeBadge
            onClick={() => setIsFilterOpen(true)}
            dateFrom={search.dateFrom}
            dateTo={search.dateTo}
          />
        }
        search={{
          value: searchText,
          onChange: setSearchText,
          placeholder: 'Number, name or publisher',
        }}
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
        createAction={createAction}
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <BatchFilterPanel filters={controller} />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the batches"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="batch"
          emptyMessage={isNarrowed ? 'No batches match these filters' : emptyMessage}
          emptyHint={
            isNarrowed
              ? 'Widen the dates, clear the ZM or change the search.'
              : 'A batch appears here once Sales publishes one.'
          }
          {...(isNarrowed
            ? {
                emptyActions: (
                  <Button type="button" variant="outline" size="sm" onClick={resetFilters}>
                    Reset filters
                  </Button>
                ),
              }
            : {})}
          isRowHighlighted={(batch) => batch.batchId === lastViewedBatchId}
          {...loadMoreProps}
        />
      )}

      {children}
    </div>
  );
}
